import { useCallback, useMemo } from 'react';

import { useLocation } from 'react-router';

import { useNavigate } from '@change-corgi/core/react/router';
import { useTracking } from '@change-corgi/core/react/tracking';

import { useStarterDashboardV2PageData } from 'src/app/pages/starterDashboardV2/pageContext';
import {
	useUpdatePersonalizedPetitionTask,
	UPDATE_TASK_ERROR_MAP,
} from 'src/app/pages/starterDashboardV2/shared/hooks';

import { TASKS_CONFIGS } from '../../../../constants';
import { useCompleteTaskByCheckbox } from '../../../../hooks/useNextSteps/useTasks/useCompleteTaskByCheckbox';
import type { Task } from '../../../../types';
import type { PetitionTaskNode } from '../useDynamicTasks/taskShapes';

type ExistingTask = Readonly<{
	id: Task['id'];
	name: Task['name'];
	status: Task['status'];
}>;

type CheckboxAction = () => Promise<void>;

export function useDynamicCheckboxAction(): (
	featureKey: string | null,
	taskId: string,
	taskTitle: string,
) => CheckboxAction {
	const {
		data,
		actions: { refreshData },
	} = useStarterDashboardV2PageData();
	const track = useTracking();
	const getCheckboxHandler = useCompleteTaskByCheckbox({
		version: 'new_ai_03_2026',
	});

	const navigate = useNavigate();
	const { pathname, search } = useLocation();

	const { mutate: updateTask } = useUpdatePersonalizedPetitionTask({
		onSuccess: () => {
			refreshData();
		},
		onError: (error) => {
			if (error === UPDATE_TASK_ERROR_MAP.UNAUTHENTICATED_ERROR.code) {
				const params = encodeURIComponent(`${pathname}${search}`);
				navigate(`/login_or_join?redirectTo=${params}`);
			}
		},
	});

	const existingTasksByName = useMemo<ReadonlyMap<string, ExistingTask>>(() => {
		const { activeTasks } = data.petition;
		if (activeTasks.__typename !== 'PetitionTasksConnection') return new Map();
		return new Map(activeTasks.nodes.map((task: PetitionTaskNode) => [task.name, task]));
	}, [data.petition]);

	const petitionId = data.petition.id;

	return useCallback(
		(featureKey: string | null, taskId: string, taskTitle: string) => {
			if (featureKey && Object.hasOwn(TASKS_CONFIGS, featureKey)) {
				const existingTask = existingTasksByName.get(featureKey);
				if (existingTask) {
					const runCompletePetitionTask = getCheckboxHandler(existingTask.id, existingTask.name, existingTask.status);
					return async () => {
						await runCompletePetitionTask();
						// Checkbox path only completed the petition task; persist the AI row too so refresh matches UI.
						const syncResult = await updateTask({
							petitionId,
							personalizedPetitionTaskId: taskId,
							status: 'COMPLETED',
						});
						if (
							syncResult.status === 'error' &&
							syncResult.error !== UPDATE_TASK_ERROR_MAP.ALREADY_COMPLETED_ERROR.code
						) {
							throw new Error(syncResult.error);
						}
					};
				}
			}
			return async () => {
				void track('petition_manage_tasks_completed', {
					task: 'mark_as_done',
					task_name: taskTitle,
					completed: 'manual',
					version: 'new_ai_03_2026',
				});
				const result = await updateTask({
					petitionId,
					personalizedPetitionTaskId: taskId,
					status: 'COMPLETED',
				});
				if (result.status === 'error' && result.error !== UPDATE_TASK_ERROR_MAP.ALREADY_COMPLETED_ERROR.code) {
					throw new Error(result.error);
				}
			};
		},
		[getCheckboxHandler, existingTasksByName, petitionId, updateTask, track],
	);
}
